import {type InjectionKey, type ComputedRef} from "vue";

/**
 * Allowed flags
 */
export const ObjectAccessFlags = [
	"view", "sensitive", "edit", "owner", "responsible", "salesman", "manager", "delete"
] as const;

export type ObjectAccessFlag = typeof ObjectAccessFlags[number];


export type ObjectAccess = {
	[K in ObjectAccessFlag]:boolean;
}

export interface WithObjectAccess {
	_access?: ObjectAccess;
	_partial?: boolean;
}

export type Secured<T> = T & WithObjectAccess;

export function isSecured<T>(obj:T):obj is Secured<T> {
	return typeof (obj as WithObjectAccess)?._access === "object";
}
export function isPartial(obj:any):boolean {
	return typeof (obj as WithObjectAccess)?._partial === "boolean" && obj._partial;
}

/** Return true if the secure object has all flags */
export function hasAccess(obj:any, ...flags:ObjectAccessFlag[]):boolean {
	if (isSecured(obj)) {
		for (const flag of flags) {
			if (!obj._access[flag]) return false;
		}
	}
	return true;
}

/** Return true if the secure object has any of flags */
export function hasAnyAccess<T>(obj:Secured<T>, ...flags:ObjectAccessFlag[]):boolean {
	if (isSecured(obj)) {
		for (const flag of flags) {
			if (obj._access[flag]) return true;
		}
		return false;
	}
	return true;
}

export function grantAccess(obj:any, ...flags:ObjectAccessFlag[]):void {
	if (typeof obj === "object") {
		if (!obj.hasOwnProperty("_access")) obj._access = {};
		for (let flag of flags) obj._access[flag] = true;
	}
}

export function revokeAccess(obj:any, ...flags:ObjectAccessFlag[]):void {
	if (typeof obj === "object") {
		if (!obj.hasOwnProperty("_access")) return;	// all is already revoked
		for (let flag of flags) {
			delete obj._access[flag];	// remove flag
		}
	}
}

export type checkAccessFunc = (...flags:ObjectAccessFlag[]) => boolean;

/** Function provided by RowObjectPage to its subview (to check access rights) */
export const checkAccessKey = Symbol() as InjectionKey<checkAccessFunc>;
export const readOnlyKey = Symbol() as InjectionKey<ComputedRef<boolean>>;

export const secureRootKey = Symbol() as InjectionKey<object | any[]>;
